import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useLayout } from '@/contexts/layout';
import { PromotionBannerData } from '@/types/promotion-banner';

const RESERVATION_FLOW_PAGES = [
  '/',
];

const calculateTimeLeft = (expirationTime: number) => {
  const difference = expirationTime - Date.now();
  let timeLeft = { days: 0, hours: 0, minutes: 0, seconds: 0 };

  if (difference > 0) {
    timeLeft = {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60),
    };
  }

  return timeLeft;
};

export const usePromotionBannerLogic = () => {
  const { setIsPromotionBannerVisible, setCouponToApply, promotionBannerData } = useLayout();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const router = useRouter();

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setIsPromotionBannerVisible(false);
  }, [setIsPromotionBannerVisible]);

  const handleCopyCoupon = useCallback(() => {
    if (promotionBannerData?.coupon_code) {
      navigator.clipboard.writeText(promotionBannerData.coupon_code);
      if (setCouponToApply) {
        setCouponToApply(promotionBannerData.coupon_code);
      }
    }
  }, [promotionBannerData, setCouponToApply]);

  useEffect(() => {
    const isOnReservationFlow = RESERVATION_FLOW_PAGES.some(path => router.pathname === path);
    let shouldBeVisible = isOnReservationFlow && promotionBannerData?.enabled;

    if (shouldBeVisible && promotionBannerData) {
      if (promotionBannerData.promotion_type === 'date_range') {
        const now = Date.now();
        const startTime = promotionBannerData.start_date ? new Date(promotionBannerData.start_date).getTime() : 0;
        const endTime = promotionBannerData.end_date ? new Date(promotionBannerData.end_date).getTime() : 0;
        if (!startTime || !endTime || now < startTime || now > endTime) {
          shouldBeVisible = false;
        }
      }
    }

    setIsVisible(!!shouldBeVisible);
    setIsPromotionBannerVisible(!!shouldBeVisible);

  }, [router.pathname, promotionBannerData, setIsPromotionBannerVisible]);

  useEffect(() => {
    if (!isVisible || !promotionBannerData) {
      return;
    }

    let expirationTimestamp = 0;

    if (promotionBannerData.promotion_type === 'date_range' && promotionBannerData.end_date) {
      expirationTimestamp = new Date(promotionBannerData.end_date).getTime();
    } else if (promotionBannerData.promotion_type === 'urgency_countdown' && promotionBannerData.countdown_duration_minutes > 0) {
      expirationTimestamp = Date.now() + promotionBannerData.countdown_duration_minutes * 60 * 1000;
    }

    if (expirationTimestamp <= Date.now()) {
      setIsVisible(false);
      setIsPromotionBannerVisible(false);
      return;
    }

    setTimeLeft(calculateTimeLeft(expirationTimestamp));

    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(expirationTimestamp);
      setTimeLeft(newTimeLeft);
      if (newTimeLeft.days <= 0 && newTimeLeft.hours <= 0 && newTimeLeft.minutes <= 0 && newTimeLeft.seconds <= 0) {
        clearInterval(timer);
        setIsVisible(false);
        setIsPromotionBannerVisible(false);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isVisible, promotionBannerData, setIsPromotionBannerVisible]);

  return { isVisible, timeLeft, handleClose, handleCopyCoupon, promotionBannerData };
};
